import { Injector, Signal, Type, WritableSignal } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  EWidget,
  IFilterValuesVisibleToWidget,
  IHeaderAction,
  TFilterSave,
  TInEffectFilter,
  TWidgetCustomParameters,
  TWidgetParameters,
  TWidgets,
} from 'scw-digital-boards-widgets';
import { IResolvedScreenLock } from '../../core/services/screen-lock.interface';
import { CountdownManager } from '../../shared/components/countdown/countdown-manager.class';
import { PageViewAndEditModeSwitchManager } from '../../shared/components/page-view-and-edit-mode-switch/page-view-and-edit-mode-switch-manager.class';
import { IDashboard } from '../settings/dashboards/dashboards.interface';
import { openDfPageOptions } from './widget-add-edit-dialog/input-components/open-df-page-configuration/open-df-page-configuration.interface';
import { WidgetAddEditDialogComponent } from './widget-add-edit-dialog/widget-add-edit-dialog.component';
import {
  EItemType,
  ETypeDependentInputs,
  TRefreshIntervalOption,
  TSubDashboardWindowSize,
} from './widget-add-edit-dialog/widget-add-edit-dialog.interface';

export interface IGridstackPositionSubmit {
  x?: number;
  y?: number;
  w: number;
  h: number;
  layout?: EDashboardLayout;
}

export interface IWidgetConfiguration {
  isLastUpdatedLabelShown: boolean;
  refreshInterval: TRefreshIntervalOption;
  gridstackOptions: IGridstackPositionSubmit;
  isAutoScrollEnabled: boolean;
  zoom: number;
  actionTracker?: IActionTracker;
  subDashboardWindowSize?: TSubDashboardWindowSize;
  openDfPageConfiguration?: keyof typeof openDfPageOptions;
}

export interface IWidgetFilterModeSave {
  modeName: string;
  filters: TFilterSave[];
  suppressedFilters: string[];
}

export interface IWidgetFilterSuppression {
  modeName: string;
  suppressedFilters: string[];
}

export interface IWidgetData<WidgetType extends EWidget = EWidget> {
  configuration: IWidgetConfiguration;
  filters: IWidgetFilterModeSave[]; // Order of modes in this array matters
  subDashboardId: number | null;
  subtitle: string | null;
  title: string | null;
  type: WidgetType;
  typeBasedConfiguration: TWidgetCustomParameters[WidgetType];
  widgetId: number | symbol;
}

export interface IActionTracker {
  boardId: number | null;
  itemTypeId: EItemType | null;
  itemCategoryId: number | null;
}

type TCustomDashboardWidgetAddPayload<WidgetType extends EWidget = EWidget> = Pick<
  IWidgetData<WidgetType>,
  'title' | 'subtitle' | 'typeBasedConfiguration' | 'type' | 'configuration' | 'filters' | 'subDashboardId'
>;

type TCustomDashboardWidgetEditPayload<WidgetType extends EWidget = EWidget> = Pick<
  IWidgetData<WidgetType>,
  'title' | 'subtitle' | 'typeBasedConfiguration' | 'configuration' | 'filters' | 'subDashboardId'
> & { id: number };

export type TWidgetSavePayload<WidgetType extends EWidget = EWidget> =
  | TCustomDashboardWidgetAddPayload<WidgetType>
  | TCustomDashboardWidgetEditPayload<WidgetType>;

export interface IWidget extends IWidgetData {
  id: number;
}

export interface IDashboardWithWidgets extends IDashboard {
  autoLock?: IResolvedScreenLock | null;
  widgets: IWidget[];
}

export type TGridItem<WidgetType extends EWidget = EWidget> = {
  component: Type<TWidgets>;
  doesWidgetUseAnyFilter: Signal<boolean>;
  gridstackOptions: IGridstackPositionSubmit & {
    widgetId: number | symbol;
  };
  injector: Injector;
  isHighlighted: Signal<boolean>;
  filtersForOpenDfPage: Signal<TInEffectFilter[]>;
  lastAppliedFilters: WritableSignal<IFilterValuesVisibleToWidget[]>;
  lastAppliedFilterSuppression: WritableSignal<IWidgetFilterSuppression[]>;
  widgetData: IWidgetData<WidgetType>;
  widgetInputs: TWidgetParameters<WidgetType>;
  headerActions: IHeaderAction[] | null;
};

export enum EWidgetInputType {
  singleSelectDropdown = 'singleSelectDropdown',
  multiselectDropdown = 'multiselectDropdown',
  number = 'number',
  string = 'string',
  boolean = 'boolean',
  other = 'other',
}

export type TFormControlValueOfInput<Input extends ETypeDependentInputs> =
  WidgetAddEditDialogComponent['formGroup']['controls'][Input]['value'];

export type TCustomParameterKeyName = TWidgetCustomParameters[EWidget] extends infer U
  ? U extends any
    ? keyof U
    : never
  : never;

export interface ITypeBasedConfigurationInput<Input extends ETypeDependentInputs> {
  editDataFormResetCustomImplementation?: (
    editDataConfiguration: TWidgetCustomParameters[EWidget],
  ) => TFormControlValueOfInput<Input>;
  rules: Validators[];
  type: EWidgetInputType;
  editDataKeyName: (widget: EWidget) => TCustomParameterKeyName;
  extractFormControlValueCustomImplementation?: (value: any) => any;
}

export interface IDashboardContentDialogData {
  subDashboardId: number;
  countdownManager: CountdownManager;
  pageViewAndEditModeSwitchManager: PageViewAndEditModeSwitchManager;
  dashboard: IDashboardWithWidgets;
}

export enum EDashboardLayout {
  fixedHeight = 1,
  fitToScreen = 2,
}
